//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// team-settings-admin-team-calendar imports
import { IContainer, IProps, IState } from "./TeamSettingsAdminTeamCalendar.d";
import {
  teamSettingsAdminTeamCalendarFragments,
  refetchQuery,
} from "./TeamSettingsAdminTeamCalendar.gql";
// other imports
import { H2, A } from "@ui";
import { Switch } from "@clockwise/design-system";
// util
import { maybeGetTeamSettingsFromOrg } from "@util/team-settings.util";
import { PostMessageManager } from "@util/post-message.util";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
// state
import { snackBarOpen } from "@actions/snack-bar.actions";
import { saveTeamSettings } from "@mutations";
// material-ui
import { Checkbox, FormControl, FormControlLabel } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsAdminTeamCalendarCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const teamSettings = maybeGetTeamSettingsFromOrg(props.org);

    this.state = {
      createOOOCalendar: teamSettings ? teamSettings.manageTeamCalendar : false,
      syncWFHEventsEnabled: teamSettings ? teamSettings.syncWFHEvents : false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private handleOOCalendarToggle = () => {
    const { team } = this.props;
    const { createOOOCalendar } = this.state;
    const newSyncValue = !createOOOCalendar;

    this.setState({ createOOOCalendar: newSyncValue }, () => {
      // call save mutation
      const successCallback = () => {
        if (
          createOOOCalendar &&
          team.userSettings &&
          team.userSettings.__typename !== "GraphEntityError" &&
          team.userSettings.useTeamCalendar
        ) {
          PostMessageManager.toggleCalendar(team.teamCalendarId, true);
        }
        this.props.snackBarOpen(`${newSyncValue ? "Created" : "Removed"} your team's calendar!`);
      };

      const failureCallback = () => {
        const msg = `Failed to ${newSyncValue ? "create" : "remove"} your team's calendar!`;
        this.props.snackBarOpen(msg);
        logger.error(msg);
      };

      this.updateTeamSettings(successCallback, failureCallback);
    });

    track(TrackingEvents.TEAMS.TEAM_SETTINGS_ADMIN_TOGGLED_OOO_CALENDAR, {
      manageTeamCalendar: newSyncValue,
    });
  };

  private handleSyncWFHEventsCheck = () => {
    const { syncWFHEventsEnabled } = this.state;
    const newSyncValue = !syncWFHEventsEnabled;

    this.setState({ syncWFHEventsEnabled: newSyncValue }, () => {
      // call save mutation
      const successCallback = () => {
        this.props.snackBarOpen(`Updated your team's calendar preferences!`);
      };

      const failureCallback = () => {
        const msg = `Failed to update your team's calendar preferences!`;
        this.props.snackBarOpen(msg);
        logger.error(msg);
      };

      this.updateTeamSettings(successCallback, failureCallback);
    });

    track(TrackingEvents.TEAMS.TEAM_SETTINGS_ADMIN_TOGGLED_WFH_SYNC, {
      syncWFHEvents: newSyncValue,
    });
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private updateTeamSettings = (successCallback?: () => void, failureCallback?: () => void) => {
    const { relay, org, team } = this.props;
    const { createOOOCalendar, syncWFHEventsEnabled } = this.state;

    let input: ISchema.ISaveTeamSettingsMutationInput = {
      orgRelayId: org.id,
      teamId: team.teamId,
      manageTeamCalendar: createOOOCalendar,
      syncWFHEvents: syncWFHEventsEnabled,
    };

    saveTeamSettings(
      relay.environment,
      input,
      successCallback ? successCallback : () => null,
      failureCallback ? failureCallback : () => null,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  public render() {
    const { team, isWebSettings } = this.props;
    const { createOOOCalendar, syncWFHEventsEnabled } = this.state;

    return (
      <div className="cw-mt-4 ">
        {!isWebSettings && (
          <>
            <H2 style={{ fontSize: 18 }}>Team availability calendar</H2>
            <div className="cw-body-base cw-text-slate-standard cw-mb-4">
              Clockwise automatically syncs individual team member’s “Out of office / Unavailable”
              events to a shared team calendar to help the team stay informed about everyone’s
              availability at a glance.{" "}
              <A href={articleUrls.teamCalendar} target="_blank">
                Learn more
              </A>
              .
            </div>
          </>
        )}
        <Switch
          checked={createOOOCalendar}
          onChange={this.handleOOCalendarToggle}
          label={
            <>
              <div>
                Create a shared “Clockwise: {team.teamName}" calendar and sync everyone’s events
              </div>
              <div className="cw-body-base cw-text-slate-standard">
                When enabled, visible in everyone’s calendars list in Google Calendar
              </div>
            </>
          }
        />
        <div className="cw-mt-1 cw-ml-14">
          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={syncWFHEventsEnabled}
                  disabled={!createOOOCalendar}
                />
              }
              onChange={this.handleSyncWFHEventsCheck}
              label={
                <span className="cw-body-lg">
                  Also sync events that Clockwise categorizes as WFH (work from home)
                </span>
              }
              labelPlacement="end"
            />
          </FormControl>
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////

const TeamSettingsAdminTeamCalendarRedux = connect(null, {
  snackBarOpen,
})(TeamSettingsAdminTeamCalendarCmpt);

//////////////////
// RELAY
//////////////////
export const TeamSettingsAdminTeamCalendar = createRefetchContainer<IContainer>(
  TeamSettingsAdminTeamCalendarRedux,
  teamSettingsAdminTeamCalendarFragments,
  refetchQuery,
);
